import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  SortByAlpha as SortIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Message as MessageIcon,
} from "@mui/icons-material";
import { useFetchAllPatients } from "../../services/api/services";
import { IPatientList } from "../../models/PatientListModel";

const PatientListPage = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [patientListState, fetchPatientList] = useFetchAllPatients();

  const patients: IPatientList[] = patientListState.data ?? [];

  useEffect(() => {
    fetchPatientList();
  }, []);

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: any) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event: any) => {
    setFilterStatus(event.target.value);
    setPage(0);
  };

  // Filter patients based on search term and status filter
  const filteredPatients = patients.filter((patient) => {
    const matchesSearch =
      patient?.patientName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getInitials = (name: any) => {
    return name
      .split(" ")
      .map((part: any) => part[0])
      .join("")
      .toUpperCase();
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>s
        <Paper
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            <TextField
              variant="outlined"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="status-filter-label">Filter by Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={filterStatus}
                label="Filter by Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="All">All Patients</MenuItem>
                <MenuItem value="New">New</MenuItem>
                <MenuItem value="Regular">Regular</MenuItem>
                <MenuItem value="Critical">Critical</MenuItem>
                <MenuItem value="Follow-up">Follow-up</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<SortIcon />}
              sx={{ borderRadius: 2 }}
            >
              Sort
            </Button>
          </Box>
        </Paper>

        {/* Patients Table */}
        <Paper
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: "#f5f7fa" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPatients
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((patient, index) => {
                    return (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar
                              sx={{
                                mr: 2,
                                bgcolor: `hsl(${1 * 40}, 70%, 80%)`,
                                color: `hsl(${1 * 40}, 70%, 30%)`,
                              }}
                            >
                              {getInitials(patient.patientName)}
                            </Avatar>
                            <Typography variant="body1" fontWeight={500}>
                              {patient.patientName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{patient.mobileNumber}</TableCell>
                        <TableCell>{patient.email}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex" }}>
                            <IconButton
                              color="primary"
                              size="small"
                              title="View Profile"
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              color="secondary"
                              size="small"
                              title="Edit Patient"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="info"
                              size="small"
                              title="Send Message"
                            >
                              <MessageIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredPatients.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {/* Stats Summary */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 4 }}>
          <Paper
            sx={{
              flex: "1 1 200px",
              p: 3,
              borderRadius: 2,
              borderLeft: "4px solid #1976d2",
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Typography variant="h6" gutterBottom>
              Total Patients
            </Typography>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {patients.length}
            </Typography>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default PatientListPage;
